import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { useSearchParams, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { format, addDays } from "date-fns";
import {
  Tab,
  TabList,
  Spinner,
  Tooltip,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";
import { ErrorCircleRegular, InfoRegular } from "@fluentui/react-icons";
import Page from "components/Page/Page";
import { Navigation } from "components/Navigation/Navigation";
import {
  ValidationErrorSidebar,
  FloatingErrorButton,
} from "components/ValidationErrorSidebar";
import {
  useValidationErrors,
  ValidationError,
  TabConfig,
} from "utils/validation/useValidationErrors";
import { useGetOffer } from "state/queries/useGetOffer";
import { QUERY_KEYS } from "library/shared";
import type { ProposalFormData } from "library/types/types";
import { mapOfferToProposalForm } from "utils/proposalTransformers";
import { useCalculationValidation } from "hooks/useCalculationValidation";
import { useTaxonomy } from "library/utils/useTaxonomy";
import { PrefillProvider, usePrefillContext } from "./contexts/PrefillContext";

import GeneralInfoTab from "./tabs/GeneralInfoTab";
import MtplQuotesTab from "./tabs/MtplQuotesTab";
import CascoQuotesTab from "./tabs/CascoQuotesTab";
import {
  createProposalValidationSchema,
  PROPOSAL_TAB_CONFIG,
} from "./useFormValidationSchema";

type TabValue = "generalInfo" | "proposal";

type ProposalMode = "mtpl" | "casco" | null;

const BlueTopBorder = styled.div`
  width: 100%;
`;

const ProposalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 125px); /* Subtract navigation height */
  width: 1500px;
`;

const TabListContainer = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border-bottom: 1px solid
    ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  padding: 12px 24px;
  flex-shrink: 0; /* Prevent shrinking */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const StyledTabList = styled(TabList)`
  flex: 1;
  .fui-TabList__content {
    gap: 0;
  }
`;

const ProposalInfo = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  white-space: nowrap;

  &.new-proposal {
    font-weight: 400;
    color: ${({ theme }) => theme.semanticColors.bodySubtext};
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-variant: small-caps;
  }
`;

const ProposalNumberText = styled.span`
  color: ${({ theme }) => theme.semanticColors.link};
  font-weight: 700;
  margin-left: 8px;
`;

const ProposalSeparator = styled.span`
  margin: 0 12px;
  color: ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  font-weight: 300;
`;

const LifecycleInfoButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.palette.themePrimary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const LifecycleInfoContent = styled.div`
  max-width: 400px;
  padding: 12px;
  line-height: 1.6;
  font-size: 13px;

  p {
    margin: 0 0 8px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
  }
`;

const TabContentWrapper = styled.div`
  flex: 1;
  overflow: hidden; /* Remove scroll from parent */
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  min-height: 0; /* Allow shrinking below content size */
`;

const TabContent = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border-radius: 8px;
  padding: 0px 22px 0px 22px;
  height: 100%; /* Fill parent height */
  display: flex;
  flex-direction: column;
`;

const ErrorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  background: #d13438;
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const LoadingBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  flex-direction: column;
  gap: 16px;
`;

const LoadingContainer = styled.div`
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  padding: 32px 48px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.semanticColors.bodyText};
  font-weight: 500;
`;

/**
 * Proposal component with mode detection for MTPL and CASCO
 *
 * Mode Detection Logic:
 * 1. For NEW proposals (no proposalId): Checks URL parameter ?type=mtpl or ?type=casco
 * 2. For EXISTING proposals (with proposalId): Uses proposalData.insuranceProduct form value
 *
 * Mode Behavior:
 * - MTPL mode: Shows General Info, MTPL Quotes, and Documents tabs
 * - CASCO mode: Shows General Info, CASCO Quotes, and Documents tabs
 * - No mode detected: Shows all tabs (default behavior)
 *
 * Usage Examples:
 * - /proposal?type=mtpl - Creates new MTPL proposal
 * - /proposal?type=casco - Creates new CASCO proposal
 * - /offer/123 - Edits existing proposal (mode from form data)
 */
const Proposal: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { offerId: proposalId } = useParams<{ offerId?: string }>();
  // Open Proposal tab by default for existing offers, General Info for new offers
  const [selectedTab, setSelectedTab] = useState<TabValue>(
    proposalId ? "proposal" : "generalInfo",
  );
  const [isErrorSidebarOpen, setIsErrorSidebarOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [proposalMode, setProposalMode] = useState<ProposalMode>(null);
  const [isCalculating, setIsCalculating] = useState(false); // Track if any calculation is in progress
  const [currentOfferId, setCurrentOfferId] = useState<string | null>(null); // Track current offer ID
  const { isPrefilling } = usePrefillContext(); // Get prefilling state
  const taxonomy = useTaxonomy(); // Get taxonomy for ID-to-code conversion
  const queryClient = useQueryClient(); // For updating cached offer data

  // Get the duplicate parameter from URL
  const duplicateOfferId = searchParams.get("duplicate");

  // Store quotes data to persist across tab switches
  const [cascoQuotes, setCascoQuotes] = useState<any[]>([]);
  const [mtplQuotes, setMtplQuotes] = useState<any[]>([]);
  const [rawMtplData, setRawMtplData] = useState<any[]>([]);

  const validationSchema = useMemo(
    () => createProposalValidationSchema(t),
    [t],
  );
  const { extractValidationErrors } = useValidationErrors(
    PROPOSAL_TAB_CONFIG as unknown as TabConfig,
  );

  // Initialize shared calculation validation hook
  const {
    validationErrors: calculationValidationErrors,
    isValid: isCalculationValid,
    validateRequest,
    clearErrors: clearCalculationErrors,
    scrollToFirstError,
    getError,
  } = useCalculationValidation({
    onValidationChange: (valid, errors) => {
      if (!valid && errors.length > 0) {
        // console.log("Calculation validation errors:", errors);
      }
    },
  });

  // Calculate error count for General Info tab badge
  const generalInfoErrorCount = useMemo(() => {
    return calculationValidationErrors.filter((error) => {
      const field = error.field.toLowerCase();
      return field.startsWith("client.") || field.startsWith("vehicle.");
    }).length;
  }, [calculationValidationErrors]);

  // Initialize React Hook Form
  const methods = useForm<ProposalFormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      proposalData: {
        insuranceProduct: "MTPL",
        proposalNumber: "",
        proposalType: "",
        proposalTemplate: "",
        proposalStatus: "",
        offerStatus: "OfferStatus.OFFER",
        rejectionReason: "",
        webTemplate: false,
      },
      vehicleData: {
        licensePlateNumber: "",
        vehicleMake: "",
        vehicleModel: "",
        productionYear: "",
        isNew: false,
        vin: "",
        objectGroup: "",
        engineCapacity: "",
        power: "",
        maximumGrossWeight: "",
        seats: "",
        bodyType: "",
        fuelType: "",
        vehicleValue: "",
        currency: "",
        includeVAT: false,
        objectUsage: "",
        vehicleTransportTypeCode: "",
        abroadUse: "",
        isLeased: false,
        leasingCompany: "",
        glassInsurance: "",
        deductible: "",
        theftDeductible: "",
        additionalEquipmentLimit: "",
        replacementCar: false,
        accidentInsurance: false,
        repairLocation: "",
        usedInTraffic: false,
        technicalPassportNumber: "",
        ownerCode: "",
        ownerName: "",
        ownerAddress: "",
        ownerEmail: "",
        ownerPhone: "",
        otherDriverAge: "",
        otherDriverExperience: "",
      },
      clientData: {
        clientType: "",
        vip: false,
        sicCode: "",
        resident: false,
        bonusMalus: "",
        clientName: "",
        clientFirstName: "",
        clientLastName: "",
        clientCode: "",
        clientEmail: "",
        licenceDate: "",
        region: "",
        clientAge: "",
        additionalRegion: "",
        drivingExperience: "",
        city: "",
        socialDiscount: "",
        cityAddress: "",
        territoryOfValidity: "",
        address: "",
        otherDriverLicenceDate: "",
        postIndex: "",
        otherDriverAge: "",
        clientPhoneNumber: "",
        isExistingInGOS: false,
      },
      additionalData: {
        proposalValidTill: "",
        paymentType: "",
        policyDeliveryType: "",
        broker: "",
        accountManager: "",
        salesPerson: "",
        responsiblePerson: "",
        family: "",
        notes: "",
        lastCall: "",
        remindAutomatically: false,
        lastEmail: "",
        createdBy: "",
        modifiedBy: "",
      },
      insuranceData: {
        previousInsuranceEndDate: "",
        startDate: format(addDays(new Date(), 1), "yyyy-MM-dd"), // Default to tomorrow
      },
      cascoSelections: {
        selectedInsurers: [
          "Balta",
          "Balcia",
          "Compensa",
          "BAN",
          "Ergo",
          "IF",
          "BTA",
        ],
        selectedPayments: ["1payment", "2payments", "4payments", "12payments"],
      },
      mtplSelections: {
        selectedInsurers: [
          "Balcia",
          "Balta",
          "BAN",
          "BTA",
          "Compensa",
          "Ergo",
          "IF",
        ],
        selectedPeriods: [
          "1month",
          "3months",
          "6months",
          "9months",
          "12months",
        ],
      },
    },
  });

  // Track if initial data has been loaded to prevent re-population on tab focus
  const hasLoadedInitialData = useRef(false);

  // Backend data loading for existing proposals
  const {
    data: offerData,
    error: offerError,
    isLoading: isLoadingOffer,
  } = useGetOffer({
    offerId: proposalId || null,
    enabled: !!proposalId, // Only fetch if we have a proposalId
  });

  // Backend data loading for duplicate proposals (when creating a copy)
  const {
    data: duplicateOfferData,
    error: duplicateOfferError,
    isLoading: isLoadingDuplicateOffer,
  } = useGetOffer({
    offerId: duplicateOfferId || null,
    enabled: !!duplicateOfferId && !proposalId, // Only fetch if we have a duplicate ID and not editing an existing proposal
  });

  // Populate form data when offer data is loaded (only once)
  useEffect(() => {
    if (offerData && proposalId && !hasLoadedInitialData.current) {
      console.log("Raw backend data received:", offerData);

      const formData = mapOfferToProposalForm(offerData, taxonomy);

      // Populate all form fields
      methods.reset(formData);

      // Set proposal mode based on loaded data
      const insuranceProduct = formData.proposalData.insuranceProduct;
      if (insuranceProduct) {
        setProposalMode(insuranceProduct.toLowerCase() as ProposalMode);
        console.log("Proposal mode set to:", insuranceProduct.toLowerCase());
      }

      console.log("Mapped form data:", formData);

      // Mark as loaded to prevent re-population
      hasLoadedInitialData.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData, proposalId, taxonomy]);

  // Update offer status when it changes (e.g., after payment)
  useEffect(() => {
    if (offerData && proposalId && hasLoadedInitialData.current) {
      const currentOfferStatus = methods.getValues("proposalData.offerStatus");
      const formData = mapOfferToProposalForm(offerData, taxonomy);
      const newOfferStatus = formData.proposalData.offerStatus;

      // Only update if status has actually changed
      if (newOfferStatus && currentOfferStatus !== newOfferStatus) {
        console.log("OfferStatus changed:", {
          from: currentOfferStatus,
          to: newOfferStatus,
        });
        methods.setValue("proposalData.offerStatus", newOfferStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData?.OfferStatus, proposalId]);

  // Populate form data when duplicating an offer (creating a copy)
  useEffect(() => {
    if (
      duplicateOfferData &&
      duplicateOfferId &&
      !proposalId &&
      !hasLoadedInitialData.current
    ) {
      console.log("Loading duplicate offer data:", duplicateOfferData);

      const formData = mapOfferToProposalForm(duplicateOfferData, taxonomy);

      // Reset fields that shouldn't be copied to a new proposal
      const cleanedFormData = {
        ...formData,
        proposalData: {
          ...formData.proposalData,
          offerId: "", // Clear offer ID for new proposal
          proposalNumber: "", // Clear proposal number for new proposal
          proposalStatus: "", // Clear status
          offerStatus: "OfferStatus.OFFER", // Set to In Progress for new proposal
          rejectionReason: undefined, // Clear rejection reason
        },
        additionalData: {
          ...formData.additionalData,
          accountManager: "", // Clear user tracking fields
          responsiblePerson: "",
          createdBy: "",
          modifiedBy: "",
          lastCall: "",
          lastEmail: "",
        },
      };

      // Populate form with cleaned data
      methods.reset(cleanedFormData);

      // Set proposal mode based on loaded data
      const insuranceProduct = cleanedFormData.proposalData.insuranceProduct;
      if (insuranceProduct) {
        setProposalMode(insuranceProduct.toLowerCase() as ProposalMode);
        console.log(
          "Duplicate proposal mode set to:",
          insuranceProduct.toLowerCase(),
        );
      }

      console.log("Populated form with duplicate data:", cleanedFormData);

      // Mark as loaded to prevent re-population
      hasLoadedInitialData.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateOfferData, duplicateOfferId, proposalId, taxonomy]);

  // Reset the ref when navigating to a different proposal
  // Store the previous proposalId to detect actual navigation vs initial load
  const prevProposalIdRef = useRef<string | undefined>(proposalId);

  useEffect(() => {
    // Only reset if we're actually navigating to a DIFFERENT proposal
    // Don't reset when going from null to a proposalId (that's the create offer flow)
    if (prevProposalIdRef.current && prevProposalIdRef.current !== proposalId) {
      console.log("Navigating to different proposal - resetting flags", {
        from: prevProposalIdRef.current,
        to: proposalId,
      });
      hasLoadedInitialData.current = false;
      // Reset currentOfferId when navigating to a different proposal
      setCurrentOfferId(null);
    }
    prevProposalIdRef.current = proposalId;
  }, [proposalId]);

  // Set currentOfferId when loading existing proposal with quotes
  useEffect(() => {
    if (offerData?.OfferId) {
      console.log(
        "Setting currentOfferId from loaded offerData:",
        offerData.OfferId,
      );
      setCurrentOfferId(offerData.OfferId);
    }
  }, [offerData]);

  // Handle offer data update after operations like email sent or save offer
  const handleOfferDataUpdate = useCallback(
    (updatedOffer: any) => {
      console.log("=== handleOfferDataUpdate called ===");
      console.log("Updated offer data:", updatedOffer);
      console.log("Updated OfferStatus (raw):", updatedOffer?.OfferStatus);

      // Update the react-query cache with the new offer data
      queryClient.setQueryData([QUERY_KEYS.offer, proposalId], updatedOffer);

      // Map the updated offer data to form format
      const formData = mapOfferToProposalForm(updatedOffer, taxonomy);
      console.log("Mapped form data:", formData);
      console.log(
        "Mapped offerStatus (code):",
        formData.proposalData.offerStatus,
      );

      // Update form fields that may have changed
      if (formData.proposalData.offerStatus) {
        console.log(
          "Updating offerStatus to:",
          formData.proposalData.offerStatus,
        );
        methods.setValue(
          "proposalData.offerStatus",
          formData.proposalData.offerStatus,
        );
      }

      // Update other metadata that might have changed
      if (formData.additionalData.modifiedBy) {
        methods.setValue(
          "additionalData.modifiedBy",
          formData.additionalData.modifiedBy,
        );
      }

      // Force a refetch to ensure the UI updates with latest data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.offer, proposalId],
      });

      toast.success(
        t("qtg.proposal.offerUpdated", "Offer updated successfully"),
      );
    },
    [queryClient, proposalId, methods, taxonomy, t],
  );

  // Handle loading errors
  useEffect(() => {
    if (offerError) {
      // console.error("Error loading proposal data:", offerError);
      toast.error(t("qtg.proposal.loadError", "Failed to load proposal data"));
    }
    if (duplicateOfferError) {
      console.error("Error loading duplicate offer data:", duplicateOfferError);
      toast.error(
        t(
          "qtg.proposal.duplicateLoadError",
          "Failed to load offer data for duplication",
        ),
      );
    }
  }, [offerError, duplicateOfferError, t]);

  // Mode detection effect
  useEffect(() => {
    // For new proposals, check URL parameter
    if (!proposalId) {
      const typeParam = searchParams.get("type");

      // If duplicating, mode will be set from duplicate offer data
      // If type parameter is provided, use it
      if (typeParam === "mtpl" || typeParam === "casco") {
        setProposalMode(typeParam);
        // Set the insurance product based on URL parameter (only if not duplicating)
        if (!duplicateOfferId) {
          methods.setValue(
            "proposalData.insuranceProduct",
            typeParam.toUpperCase(),
          );
        }
      }
    } else {
      // For existing proposals, get mode from form data
      const insuranceProduct = methods.watch("proposalData.insuranceProduct");
      if (insuranceProduct) {
        setProposalMode(insuranceProduct.toLowerCase() as ProposalMode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId, searchParams, duplicateOfferId]);

  // Watch for changes in insurance product for existing proposals
  useEffect(() => {
    if (proposalId) {
      const subscription = methods.watch((value, { name }) => {
        if (
          name === "proposalData.insuranceProduct" &&
          value.proposalData?.insuranceProduct
        ) {
          setProposalMode(
            value.proposalData.insuranceProduct.toLowerCase() as ProposalMode,
          );
        }
      });
      return () => subscription.unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  // Auto-clear validation errors when user fixes form values
  useEffect(() => {
    if (calculationValidationErrors.length === 0) return;

    let timeoutId: NodeJS.Timeout;

    const subscription = methods.watch(() => {
      // Debounce validation to avoid excessive re-validation during typing
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const formData = methods.getValues();
        const {
          transformProposalToCalculationRequest,
        } = require("utils/proposalTransformers");

        try {
          // Determine selected products based on proposal mode
          const selectedProducts = proposalMode ? [proposalMode] : ["casco"];

          const requestBody = transformProposalToCalculationRequest(
            formData,
            taxonomy as any,
            [], // Empty insurers for validation check
            selectedProducts, // Pass product type based on proposal mode
            [], // Empty months
            undefined, // No userInfo needed for validation-only calls
          );

          // Re-validate with current form data
          validateRequest(requestBody);
        } catch (error) {
          // Ignore transformation errors during typing
          console.debug("Validation check skipped:", error);
        }
      }, 500); // Wait 500ms after user stops typing
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [
    calculationValidationErrors.length,
    taxonomy,
    validateRequest,
    proposalMode,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Helper function to determine tab visibility based on proposal mode
  const getVisibleTabs = useCallback((mode: ProposalMode): TabValue[] => {
    // Always show General Info and Proposal tabs
    const tabs: TabValue[] = ["generalInfo", "proposal"];

    return tabs;
  }, []);

  const visibleTabs = useMemo(
    () => getVisibleTabs(proposalMode),
    [proposalMode, getVisibleTabs],
  );

  const onTabSelect = useCallback(
    (_event: any, data: { value: unknown }) => {
      // Prevent tab switching during calculation
      if (isCalculating) {
        toast.warning(
          t(
            "qtg.proposal.calculationInProgress",
            "Please wait for the calculation to complete before switching tabs",
          ),
        );
        return;
      }

      // Prevent tab switching during prefill
      if (isPrefilling) {
        toast.warning(
          t(
            "qtg.proposal.prefillInProgress",
            "Please wait for the prefill to complete before switching tabs",
          ),
        );
        return;
      }

      setSelectedTab(data.value as TabValue);
    },
    [isCalculating, isPrefilling, t],
  );

  // Debug logging for mode detection
  useEffect(() => {
    // console.log("Proposal Mode Detection:");
    // console.log("- Proposal ID:", proposalId);
    // console.log("- URL type parameter:", searchParams.get("type"));
    // console.log(
    //   "- Insurance Product:",
    //   methods.watch("proposalData.insuranceProduct")
    // );
    // console.log("- Detected Mode:", proposalMode);
    // console.log("- Visible Tabs:", visibleTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalMode, proposalId, searchParams, visibleTabs]);

  // Auto-switch to first visible tab if current tab is not visible
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(selectedTab)) {
      const firstVisibleTab = visibleTabs[0];
      if (firstVisibleTab) {
        setSelectedTab(firstVisibleTab);
      }
    }
  }, [visibleTabs, selectedTab]);

  // Form submit handler
  const onSubmit = methods.handleSubmit((data) => {
    // console.log("Form submitted:", data);
    toast.success(t("qtg.proposal.saveSuccess"));
  });

  // Navigation buttons - removed, actions now in tabs
  const navigationButtons = undefined;

  // Handle error click navigation
  const handleErrorClick = useCallback((error: ValidationError) => {
    setIsErrorSidebarOpen(false);

    if (error.tabName) {
      // Navigate to tab containing the error
      setSelectedTab(error.tabName as TabValue);

      // Focus the field after a short delay to ensure tab change is complete
      setTimeout(() => {
        const fieldElement = document.querySelector(
          `[name="${error.fieldPath}"]`,
        );
        if (fieldElement) {
          (fieldElement as HTMLElement).focus();
          fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  }, []);

  const renderTabContent = () => {
    switch (selectedTab) {
      case "generalInfo":
        return (
          <TabContent>
            <GeneralInfoTab
              validationErrors={calculationValidationErrors}
              getError={getError}
              clearErrors={clearCalculationErrors}
              scrollToFirstError={scrollToFirstError}
              isPrefilling={isPrefilling}
              isNewProposal={!proposalId}
              onContinueToProposal={() => setSelectedTab("proposal")}
              offerId={currentOfferId}
            />
          </TabContent>
        );
      case "proposal":
        // Show MTPL or CASCO quotes tab based on proposal mode
        return (
          <TabContent>
            {proposalMode === "casco" ? (
              <CascoQuotesTab
                offerData={offerData}
                onCalculationStateChange={setIsCalculating}
                onOfferIdChange={setCurrentOfferId}
                onOfferDataUpdate={handleOfferDataUpdate}
                validateRequest={validateRequest}
                validationErrors={calculationValidationErrors}
                isValid={isCalculationValid}
                clearErrors={clearCalculationErrors}
                scrollToFirstError={scrollToFirstError}
                getError={getError}
                quotes={cascoQuotes}
                setQuotes={setCascoQuotes}
              />
            ) : (
              <MtplQuotesTab
                offerData={offerData}
                onCalculationStateChange={setIsCalculating}
                onOfferIdChange={setCurrentOfferId}
                onOfferDataUpdate={handleOfferDataUpdate}
                validateRequest={validateRequest}
                validationErrors={calculationValidationErrors}
                isValid={isCalculationValid}
                clearErrors={clearCalculationErrors}
                scrollToFirstError={scrollToFirstError}
                getError={getError}
                quotes={mtplQuotes}
                setQuotes={setMtplQuotes}
                rawMtplData={rawMtplData}
                setRawMtplData={setRawMtplData}
                isNewProposal={!proposalId}
              />
            )}
          </TabContent>
        );
      default:
        return (
          <TabContent>
            <GeneralInfoTab
              validationErrors={calculationValidationErrors}
              getError={getError}
              clearErrors={clearCalculationErrors}
              scrollToFirstError={scrollToFirstError}
              offerId={currentOfferId}
            />
          </TabContent>
        );
    }
  };

  // Who marked the offer as error and when (the offer can be viewed, but no operation is possible)
  const getMarkedAsErrorText = useCallback(() => {
    if (!offerData?.MarkedAsErrorAt) {
      return t(
        "qtg.proposal.markedAsError.noDate",
        "This offer is marked as error.",
      );
    }
    const date = format(new Date(offerData.MarkedAsErrorAt), "dd.MM.yyyy HH:mm");
    const userFullName = offerData.MarkedAsErrorByUserFullName || "";
    const username = offerData.MarkedAsErrorByUsername || "";
    return userFullName || username
      ? t("qtg.proposal.lifecycle.markedAsError", {
          userFullName,
          username,
          date,
        })
      : t("qtg.proposal.lifecycle.markedAsErrorNoUser", { date });
  }, [offerData, t]);

  // Generate lifecycle information text
  const getLifecycleInfo = useCallback(() => {
    if (!offerData) return null;

    const messages: React.ReactNode[] = [];

    // Invalidation info (if invalidated)
    if (offerData.IsInvalidated && offerData.InvalidatedAt) {
      const date = format(
        new Date(offerData.InvalidatedAt),
        "dd.MM.yyyy HH:mm",
      );
      const hasUserInfo =
        offerData.InvalidatedByUserFullName || offerData.InvalidatedByUsername;

      if (hasUserInfo) {
        const userFullName = offerData.InvalidatedByUserFullName || "";
        const username = offerData.InvalidatedByUsername || "";
        messages.push(
          <p key="invalidated">
            <strong>
              {t("qtg.proposal.lifecycle.invalidated", {
                userFullName,
                username,
                date,
              })}
            </strong>
          </p>,
        );
      } else {
        messages.push(
          <p key="invalidated">
            <strong>
              {t("qtg.proposal.lifecycle.invalidatedNoUser", { date })}
            </strong>
          </p>,
        );
      }
    }

    // Marked as error info (if marked)
    if (offerData.IsMarkedAsError && offerData.MarkedAsErrorAt) {
      messages.push(
        <p key="markedAsError">
          <strong>{getMarkedAsErrorText()}</strong>
        </p>,
      );
    }

    // Offer requested info (always show if available)
    if (offerData.OfferRequestedAt) {
      const date = format(
        new Date(offerData.OfferRequestedAt),
        "dd.MM.yyyy HH:mm",
      );
      const hasUserInfo =
        offerData.OfferRequestedByUserFullName ||
        offerData.OfferRequestedByUsername;

      if (hasUserInfo) {
        const userFullName = offerData.OfferRequestedByUserFullName || "";
        const username = offerData.OfferRequestedByUsername || "";
        messages.push(
          <p key="requested">
            {t("qtg.proposal.lifecycle.offerRequested", {
              userFullName,
              username,
              date,
            })}
          </p>,
        );
      } else {
        messages.push(
          <p key="requested">
            {t("qtg.proposal.lifecycle.offerRequestedNoUser", { date })}
          </p>,
        );
      }
    }

    // Last modified info (always show if available)
    if (offerData.LastModifiedAt) {
      const date = format(
        new Date(offerData.LastModifiedAt),
        "dd.MM.yyyy HH:mm",
      );
      const hasUserInfo =
        offerData.LastModifiedByUserFullName ||
        offerData.LastModifiedByUsername;

      if (hasUserInfo) {
        const userFullName = offerData.LastModifiedByUserFullName || "";
        const username = offerData.LastModifiedByUsername || "";
        messages.push(
          <p key="modified">
            {t("qtg.proposal.lifecycle.lastModified", {
              userFullName,
              username,
              date,
            })}
          </p>,
        );
      } else {
        messages.push(
          <p key="modified">
            {t("qtg.proposal.lifecycle.lastModifiedNoUser", { date })}
          </p>,
        );
      }
    }

    return messages.length > 0 ? (
      <LifecycleInfoContent>{messages}</LifecycleInfoContent>
    ) : null;
  }, [offerData, t, getMarkedAsErrorText]);

  const hasLifecycleInfo = useMemo(() => {
    if (!offerData) return false;
    return !!(
      (offerData.IsInvalidated && offerData.InvalidatedAt) ||
      (offerData.IsMarkedAsError && offerData.MarkedAsErrorAt) ||
      offerData.OfferRequestedAt ||
      offerData.LastModifiedAt
    );
  }, [offerData]);

  return (
    <>
      {/* Loading backdrop when fetching proposal data */}
      {isLoadingOffer && proposalId && (
        <LoadingBackdrop>
          <LoadingContainer>
            <Spinner size="large" />
            <LoadingText>
              {t("qtg.proposal.loadingData", "Loading proposal data...")}
            </LoadingText>
          </LoadingContainer>
        </LoadingBackdrop>
      )}

      {/* Loading backdrop when fetching duplicate offer data */}
      {isLoadingDuplicateOffer && duplicateOfferId && (
        <LoadingBackdrop>
          <LoadingContainer>
            <Spinner size="large" />
            <LoadingText>
              {t(
                "qtg.proposal.loadingDuplicateData",
                "Loading offer data for duplication...",
              )}
            </LoadingText>
          </LoadingContainer>
        </LoadingBackdrop>
      )}

      {/* Loading backdrop during prefill action */}
      {isPrefilling && (
        <LoadingBackdrop>
          <LoadingContainer>
            <Spinner size="large" />
            <LoadingText>
              {t("qtg.proposal.prefilling", "Prefilling vehicle data...")}
            </LoadingText>
          </LoadingContainer>
        </LoadingBackdrop>
      )}

      <Navigation
        rightSideButtons={navigationButtons}
        showCascoConditionsLink={false}
      />
      <Page sidebar={null}>
        <BlueTopBorder />
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <ProposalWrapper>
              {offerData?.IsMarkedAsError && (
                <MessageBar
                  intent="error"
                  data-help="offer-marked-as-error"
                  style={{ marginBottom: "8px" }}
                >
                  <MessageBarBody>
                    <MessageBarTitle>
                      {t("qtg.proposal.markedAsError.title", "Marked as error")}
                    </MessageBarTitle>
                    {getMarkedAsErrorText()}{" "}
                    {t(
                      "qtg.proposal.markedAsError.readOnly",
                      "The offer can only be viewed; no other operation is possible until an administrator unmarks it in the offers list.",
                    )}
                  </MessageBarBody>
                </MessageBar>
              )}
              <TabListContainer>
                <StyledTabList
                  selectedValue={selectedTab}
                  onTabSelect={onTabSelect}
                >
                  {visibleTabs.includes("generalInfo") && (
                    <Tab value="generalInfo" data-help="offer-tab-general">
                      {t("qtg.proposal.tabs.generalInfo.title")}
                      {generalInfoErrorCount > 0 &&
                        selectedTab !== "generalInfo" && (
                          <ErrorBadge>
                            <ErrorCircleRegular fontSize={14} />
                            {generalInfoErrorCount}
                          </ErrorBadge>
                        )}
                    </Tab>
                  )}
                  {visibleTabs.includes("proposal") && (
                    <Tab value="proposal" data-help="offer-tab-offer">
                      {t("qtg.proposal.tabs.proposal.title", "Proposal")}
                    </Tab>
                  )}
                </StyledTabList>

                {/* Proposal info on the right side */}
                {proposalId && methods.watch("proposalData.proposalNumber") ? (
                  <ProposalInfo
                    data-help="offer-header-info"
                    data-help-status={methods.watch("proposalData.offerStatus")}
                    data-help-product={methods.watch("proposalData.insuranceProduct")?.toUpperCase()}
                  >
                    # {methods.watch("proposalData.proposalNumber")}
                    {hasLifecycleInfo && (
                      <Tooltip
                        content={getLifecycleInfo() || <div />}
                        relationship="description"
                        positioning="below"
                      >
                        <LifecycleInfoButton
                          type="button"
                          aria-label="Proposal lifecycle information"
                        >
                          <InfoRegular style={{ fontSize: "16px" }} />
                        </LifecycleInfoButton>
                      </Tooltip>
                    )}
                    {(() => {
                      const invoiceNumber =
                        proposalMode === "casco"
                          ? offerData?.Invoice?.Casco?.Request?.InvoiceNumber
                          : offerData?.Invoice?.Mtpl?.Request?.InvoiceNumber;
                      return invoiceNumber ? (
                        <>
                          <ProposalSeparator>|</ProposalSeparator>
                          {invoiceNumber}
                        </>
                      ) : null;
                    })()}
                    {offerData?.Invoice?.GroupInvoice?.Request?.InvoiceNumber && (
                      <>
                        <ProposalSeparator>|</ProposalSeparator>
                        {t("qtg.groupInvoice.headerLabel", "Group invoice")}{" "}
                        {offerData.Invoice.GroupInvoice.Request.InvoiceNumber}
                      </>
                    )}
                    <ProposalSeparator>|</ProposalSeparator>
                    {methods
                      .watch("proposalData.insuranceProduct")
                      ?.toUpperCase() || ""}
                    {methods.watch("proposalData.offerStatus") && (
                      <>
                        <ProposalSeparator>|</ProposalSeparator>
                        <ProposalNumberText>
                          {t(
                            `${methods.watch("proposalData.offerStatus")}`,
                            methods.watch("proposalData.offerStatus") || "",
                          )}
                        </ProposalNumberText>
                      </>
                    )}
                  </ProposalInfo>
                ) : (
                  <ProposalInfo className="new-proposal" data-help="offer-header-info">
                    {currentOfferId ? currentOfferId : "NEW"}
                    <ProposalSeparator>|</ProposalSeparator>
                    {proposalMode === "casco"
                      ? t("qtg.proposal.casco", "CASCO")
                      : t("qtg.proposal.mtpl", "MTPL")}
                    <ProposalSeparator>|</ProposalSeparator>
                    <ProposalNumberText>
                      {t("OfferStatus.OFFER")}
                    </ProposalNumberText>
                  </ProposalInfo>
                )}
              </TabListContainer>

              <TabContentWrapper>{renderTabContent()}</TabContentWrapper>
            </ProposalWrapper>

            {/* Validation error components */}
            <ValidationErrorSidebar
              isOpen={isErrorSidebarOpen}
              onClose={() => setIsErrorSidebarOpen(false)}
              errors={validationErrors}
              calculationMessages={[]}
              onErrorClick={handleErrorClick}
            />

            <FloatingErrorButton
              errorCount={validationErrors.length}
              warningCount={0}
              onClick={() => setIsErrorSidebarOpen(true)}
            />
          </form>
        </FormProvider>
      </Page>
    </>
  );
};

const ProposalWithProvider: React.FC = () => {
  return (
    <PrefillProvider>
      <Proposal />
    </PrefillProvider>
  );
};

export default ProposalWithProvider;
